import { StrapiClientConfig } from '../types/strapi.types';

/**
 * Configuración por defecto del cliente
 */
let clientConfig: StrapiClientConfig | null = null;

/**
 * Errores personalizados para el cliente HTTP
 */
export class StrapiApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public statusText: string,
    public data?: unknown
  ) {
    super(message);
    this.name = 'StrapiApiError';
  }
}

/**
 * Configura el cliente de Strapi globalmente
 */
export function configureStrapiClient(config: StrapiClientConfig): void {
  clientConfig = config;
}

/**
 * Obtiene la configuración actual del cliente
 */
export function getStrapiConfig(): StrapiClientConfig {
  if (!clientConfig) {
    throw new Error('Strapi client not configured. Call configureStrapiClient() first.');
  }
  return clientConfig;
}

/**
 * Verifica si el cliente está configurado
 */
export function isStrapiConfigured(): boolean {
  return clientConfig !== null;
}

/**
 * Cliente HTTP para Strapi con configuración de autenticación
 */
export async function strapiClient<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const config = getStrapiConfig();

  const url = endpoint.startsWith('http')
    ? endpoint
    : `${config.baseUrl}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${config.token}`,
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorData: unknown;
    try {
      errorData = await response.json();
    } catch {
      errorData = await response.text();
    }

    throw new StrapiApiError(
      `API Error: ${response.status} ${response.statusText}`,
      response.status,
      response.statusText,
      errorData
    );
  }

  return response.json();
}

/**
 * GET request helper
 */
export async function strapiGet<T>(endpoint: string, queryString?: string): Promise<T> {
  const url = queryString ? `${endpoint}?${queryString}` : endpoint;
  return strapiClient<T>(url, { method: 'GET' });
}

/**
 * POST request helper
 */
export async function strapiPost<T>(endpoint: string, data: unknown): Promise<T> {
  return strapiClient<T>(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * PUT request helper
 */
export async function strapiPut<T>(endpoint: string, data: unknown): Promise<T> {
  return strapiClient<T>(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * DELETE request helper
 */
export async function strapiDelete<T>(endpoint: string): Promise<T> {
  return strapiClient<T>(endpoint, { method: 'DELETE' });
}
